import { useCallback, useEffect, useRef, useSyncExternalStore } from 'react';
import { Search, Loader2, X } from '../../icons';
import { tProject, getProjectLang, subscribeProjectLang } from '../../lib/project-i18n';
import './ProjectSearchBox.css';

/**
 * ⭐ **프로젝트 통합 검색창 — 공용 부품** (2026-10-07 · 이 판은 ★ 떼어 내기만 한다).
 *
 * ## ⛔ 왜 떼나 — 「재사용」이 공짜가 아니었다
 *
 * ```
 * 떼기 전: 마크업은 ProjectHomeSpaces.tsx 안에 · CSS 는 ProjectHomeSpaces.css:113~147 에
 *   ⇒ 그 css 를 읽는 곳은 ★ ProjectHomeSpaces.tsx:8 «한 곳»뿐
 *   ⇒ ⇒ ⛔ 그래서 3c 위젯 홈(운영이 그리는 홈)이 그 검색창을 쓰려면 ★ 두 벌로 베끼게 된다
 * ```
 * ⇒ ★ 그래서 **마크업 ＋ 그 css 를 한 벌로** 묶어 둔다. 부품이 제 css 를 `import` 하므로,
 *   쓰는 쪽은 **`import` 한 줄**로 모양까지 함께 받는다.
 *
 * ## ⚠ 이 판은 ★ 「동작 변화 0」이다 — 쓰는 곳이 ⭐ 여전히 «한 곳»
 *
 * 운영 홈에 두는 것·「찾기」로 결과 화면에 보내는 것은 **다음 판**이다.
 * ⇒ 그래서 `onSubmit` 같은 칸을 **미리 만들지 않았다** — 쓰는 곳이 없는 칸은 사보타주로
 *   갈리지 않고, 그 갈래가 「죽은 코드」로 남는다([[only-caller-is-its-own-test]]).
 *
 * ## ⚠ 이름은 ★ 그대로 둔다 — `.pj-space-search` · `project.home.aw001.search_*`
 *
 * ```
 * 열쇠 이름에 aw001 이 들어 있고 class 이름에 space 가 들어 있다 — 둘 다 AW-001 쪽 이름이다.
 * ⇒ ⛔ 그래도 «안 바꾼다». 바꾸면 이 판의 ★ 「동작 변화 0」을 글자로 못 잠근다
 *   (바뀐 글자가 «옮긴 것»인지 «고친 것»인지 diff 로 갈리지 않는다).
 * ⇒ ★ 그리고 그 글귀는 두 홈이 ⭐ «같은 약속»을 하는 것이라 값을 나눌 까닭도 없다
 *   (「프로젝트명 · 담당자 · 태스크로 찾기」).
 * ⇒ ⇒ 이름을 고치는 것은 ★ 따로 할 일이다.
 * ```
 *
 * ## ⭐⭐ 2026-10-10 — ★ 정본(AW-005)이 이 검색창에 더한 셋을 ⇒ ⭐ «선택 칸»으로 받는다
 *
 * ```
 * ⛔ 정본(feature/dev-syk)은 ★ 이 떼어 내기를 ⭐ 모른다 ⇒ ⇒ 검색창을 ProjectHomeSpaces 안에
 *    ★ «인라인»으로 그린다(정본 ProjectHomeSpaces.tsx:522-556)
 * ⇒ ⇒ ⛔ 그래서 ★ 정본을 글자 그대로 덮으면 ⇒ ⭐ 마크업·CSS 가 ★ «두 벌»이 된다
 *    ⇒ ⇒ ⇒ 그것을 ★ ProjectHomeSpaces.css:112 가 ⭐ 이미 ⛔ 금지해 두었다
 * ⇒ ✅ 그래서 ★ 정본이 «더한 것»만 ⇒ ⭐ 이 부품에 ★ 선택 칸으로 들인다(관제 결정 ⓑ · 2026-10-10)
 *      searching  ⇒ 돌아가는 스피너      (정본 :541)
 *      clearable  ⇒ ✕ 지우기 단추        (정본 :542-551)
 *      shortcuts  ⇒ Ctrl/⌘+K · `/` 로 포커스 · Esc 로 지우기 ＋ 빈칸일 때 단축키 힌트 (정본 :404-418 · :552-554)
 * ⇒ ⭐⭐ 세 칸 모두 ★ «안 주면 종전과 글자까지 같은 DOM»이다 ⇒ ⇒ 쓰는 곳 셋 중 둘(ProjectPage ·
 *      ProjectSearchPage)은 ★ 한 글자도 안 바꿨다. ⇒ ⇒ 그 불변식을 ⭐ ProjectSearchBox.test.tsx 가 잠근다.
 * ⇒ ⚠ 그리고 ★ 정본에 «없는» 것(`onSubmit`·`-go`·`-form`)은 ⭐ 지우지 않는다 —
 *      ⇒ ⇒ 「그대로 이관」은 ★ 빼기가 ⛔ 아니다(관제 조건 ③).
 * ```
 */
export interface ProjectSearchBoxProps {
  value: string;
  onChange: (next: string) => void;
  /**
   * ⭐ **있으면 「찾기」를 그리고, 없으면 안 그린다** (2026-10-07 에 더했다).
   *
   * ```
   * 안 주는 쪽  AW-001 홈 — 적는 «그 자리에서» 카드를 거른다 ⇒ 보낼 곳이 없다
   * 주는 쪽     운영 홈(3c) · 결과 화면 — ★ 눌러서 /project/search?q= 로 간다
   * ```
   * ⇒ ⭐ 그래서 **두 갈래 다 쓰는 곳이 있다** — 어느 쪽도 죽은 코드가 아니다.
   * ⇒ ⚠ 빈 글자로는 **안 보낸다**(아래 `canSubmit`) — 빈 질의는 아무것도 알려 주지 않는다.
   *
   * ## ⭐ 「누르면 간다」가 ★ 디바운스를 ⭐ 없앤다
   *
   * 서버 라우트 머리말이 **「타이핑마다 도는 read-only 조회라 audit 을 걸면 폭증한다」**고
   * 적어 두었다. ⇒ ★ 그 전제는 **글자마다 부를 때만** 생긴다.
   * ⇒ ⇒ ⭐ 눌러서 가면 그 전제가 **애초에 안 생긴다.** 그리고 「몇 ms 뒤에 부른다」는
   *   사보타주로 안 갈리는데(시간값), **「적어도 안 부른다」는 갈린다.**
   */
  onSubmit?: (q: string) => void;
  /**
   * ⭐ 2026-10-10 — 서버 검색이 ★ «도는 중»이면 스피너를 세운다 (정본 AW-005 ②).
   * ⇒ ⚠ 안 주면 스피너 자리가 ★ 아예 없다 — ⇒ 종전 DOM 과 같다.
   */
  searching?: boolean;
  /**
   * ⭐ 2026-10-10 — 글자가 있을 때 ★ ✕ 단추를 그린다 (정본 AW-005).
   * ⇒ 누르면 ★ `onChange('')` ＋ ⭐ 입력칸으로 포커스를 돌려준다(정본과 같다).
   * ⇒ ⚠ `type="search"` 가 브라우저 기본 ✕ 를 하나 더 그리므로, CSS 가 그것을 지운다.
   */
  clearable?: boolean;
  /**
   * ⭐ 2026-10-10 — 단축키를 산다 ⇒ ★ `Ctrl/⌘+K`·`/` 로 포커스 · `Esc` 로 지우고 포커스 놓기.
   * ⇒ ⇒ 그리고 ★ 빈칸일 때 힌트를 ⭐ 「단축키 Ctrl+K」로 바꾼다(정본 :552-554).
   *
   * ## ⚠ 적어 둘 것 셋 (관제 조건 ② · 2026-10-10)
   *
   * ```
   * ⓐ `document` 전역이다 ⇒ ⇒ ★ «이 부품이 떠 있는 동안만» 산다
   *    ⇒ 떠날 때 removeEventListener 로 걷는다 ⇒ ⇒ 다른 화면의 Ctrl+K 를 ⛔ 안 가로챈다
   *    ⇒ ⚠ deps 는 ★ 정본의 `[]` 가 아니라 ⭐ `[shortcuts]` 다 — ⇒ 칸이 꺼진 쪽은 ★ 리스너를
   *       ⇒ ⇒ ⭐ «달지도 않는다»(정본은 늘 단다 — 정본엔 ★ 칸이 없으니 그럴 수밖에 없었다)
   * ⓑ ⛔ Ctrl/⌘+K 에는 ★ typing 가드가 ⭐ 없다 — ⇒ 글을 적는 중에도 ★ 포커스를 가져온다
   *    ⇒ ⇒ ✅ 그것이 ★ 의도다(정본 그대로) — ⇒ ⭐ 수식키를 누른 Ctrl+K 는 ★ 「지금 검색하겠다」는
   *       ⇒ ⇒ 분명한 뜻이고, 그 꼴이 ★ 다른 제품에서도 ⭐ 흔하다(Slack·GitHub·VS Code)
   * ⓒ ⭐ `/` 에는 ★ typing 가드가 ⭐ 있다 — ⇒ ⇒ ★ 그 비대칭도 ⭐ 의도다
   *    ⇒ ⇒ 까닭 ⇒ ★ `/` 는 ⭐ «글자»다 ⇒ ⇒ 가드가 없으면 ★ 날짜·경로를 적다가 ⭐ 글자를 잃는다
   *    ⇒ ⇒ ⇒ 즉 ⓑ 와 ⓒ 를 가르는 것은 ★ 「그 글쇠가 ⭐ 글자인가」다
   * ```
   * ⇒ ⚠ 우리 프론트에 종전 Ctrl/⌘+K·`/` 단축키는 ★ 0 건이었다(관제 실측) ⇒ ⇒ 가로챌 것이 없다.
   */
  shortcuts?: boolean;
}

export function ProjectSearchBox({
  value, onChange, onSubmit, searching = false, clearable = false, shortcuts = false,
}: ProjectSearchBoxProps) {
  useSyncExternalStore(subscribeProjectLang, getProjectLang, getProjectLang);
  const canSubmit = value.trim().length > 0;
  const inputRef = useRef<HTMLInputElement>(null);

  const clear = useCallback(() => {
    onChange('');
    inputRef.current?.focus();
  }, [onChange]);

  // ⭐ 단축키 — `shortcuts` 를 준 쪽에서만 산다. 안 주면 리스너를 ★ 달지도 않는다.
  useEffect(() => {
    if (!shortcuts) return undefined;
    // ⚠ 아래 몸통은 ★ 정본 ProjectHomeSpaces.tsx:403-418 ⭐ 글자 그대로다 — ⇒ 손으로 바꾸지 않았다.
    function onKey(e: KeyboardEvent) {
      const el = e.target as HTMLElement | null;
      const typing = !!el && (
        el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable
      );
      if ((e.ctrlKey || e.metaKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        inputRef.current?.focus();
        inputRef.current?.select();
        return;
      }
      if (e.key === '/' && !typing && !e.ctrlKey && !e.metaKey && !e.altKey) {
        e.preventDefault();
        inputRef.current?.focus();
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [shortcuts]);

  // ⚠ form 으로 감싸면 Enter 가 «공짜로» 따라온다 — 손으로 onKeyDown 을 달지 않는다.
  //   onSubmit 이 없으면 form 도 안 쓴다(AW-001 홈의 DOM 을 그대로 두려는 것 — 이 판의 회귀 금지).
  const box = (
    <div className="pj-space-search">
      <Search size={18} aria-hidden />
      <input
        ref={inputRef}
        type="search"
        className="pj-space-search-input"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={shortcuts ? (e) => {
          // Esc — 한 번에 지우고 포커스를 놓는다. 검색 결과에서 빠져나오는 가장 빠른 길.
          if (e.key === 'Escape') {
            e.preventDefault();
            onChange('');
            e.currentTarget.blur();
          }
        } : undefined}
        placeholder={tProject('project.home.aw001.search_placeholder')}
        aria-label={tProject('project.home.aw001.search_placeholder')}
      />
      {searching && <Loader2 size={16} className="pj-space-search-spin" aria-hidden />}
      {clearable && value && (
        <button
          type="button"
          className="pj-space-search-clear"
          onClick={clear}
          aria-label={tProject('project.home.aw005.search_clear')}
          data-tip={tProject('project.home.aw005.search_clear')}
        >
          <X size={16} aria-hidden />
        </button>
      )}
      <span className="pj-space-search-hint">
        {shortcuts && !value
          ? tProject('project.home.aw005.search_shortcut')
          : tProject('project.home.aw001.search_hint')}
      </span>
      {onSubmit && (
        <button
          type="submit"
          className="pj-button pj-button--primary pj-space-search-go"
          disabled={!canSubmit}
        >
          {tProject('project.search.submit')}
        </button>
      )}
    </div>
  );

  if (!onSubmit) return box;

  return (
    <form
      className="pj-space-search-form"
      role="search"
      onSubmit={(e) => { e.preventDefault(); if (canSubmit) onSubmit(value.trim()); }}
    >
      {box}
    </form>
  );
}
